import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { SubHubGrid, ContentSection, FaqBlock, buildFaqLd, buildBreadcrumbLd, ResultCard, ReservedResultsArea, inputCls } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, WhatsAppShareButton, useAnalyzeGate } from "@/components/age-calculator/premium";
import { PregnancyHubTabs } from "@/components/pregnancy/PregnancyHubTabs";

const TITLE = "Gestational Age Calculator - LMP Pregnancy Tracker";
const DESC = "Track your gestational age and estimate your official baby due date based on your Last Menstrual Period (LMP) or conception date.";

const FAQS = [
  { q: "How is gestational age calculated from LMP?", a: "Gestational age counts from the first day of your Last Menstrual Period (LMP), not the day of conception. The number of days elapsed since LMP is divided by 7 to give completed weeks plus remaining days." },
  { q: "How accurate is the Estimated Due Date (EDD)?", a: "The Naegele-rule EDD (LMP + 280 days) is accurate to within about ±5 days for people with regular 28-day cycles. Longer or shorter cycles shift the date; this calculator adjusts EDD for your average cycle length." },
  { q: "When does each trimester start?", a: "First trimester: weeks 1–13. Second trimester: weeks 14–27. Third trimester: weeks 28 to delivery. A full-term pregnancy is 37–42 weeks." },
  { q: "What if I don't remember my LMP?", a: "Book a dating ultrasound. A first-trimester scan measures crown-rump length and gives a gestational age accurate to within ±3–5 days, which is more reliable than LMP recall." },
];

export const Route = createFileRoute("/age-calculator/gestational-pregnancy")({
  head: () => leafHead({ path: "/age-calculator/gestational-pregnancy", title: TITLE, description: DESC, scripts: [buildFaqLd(FAQS), buildBreadcrumbLd([{ name: "Home", url: "https://drhint.com/" }, { name: "Age Calculator", url: "https://drhint.com/age-calculator" }, { name: "Gestational & Pregnancy", url: "https://drhint.com/age-calculator/gestational-pregnancy" }])] }),
  component: Page,
  errorComponent: () => (
    <div className="mx-auto max-w-lg p-8 text-center">
      <h1 className="text-2xl font-bold">Couldn't calculate that date</h1>
      <p className="mt-2 text-muted-foreground">Please refresh and enter a valid Last Menstrual Period date (within the last 42 weeks).</p>
    </div>
  ),
});

function Page() {
  const today = new Date().toISOString().slice(0, 10);
  const [lmp, setLmp] = useState("");
  const [cycle, setCycle] = useState(28);
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const canGo = !!lmp;

  const r = useMemo(() => {
    if (!lmp) return null;
    const a = new Date(lmp);
    if (isNaN(a.getTime())) return null;
    const now = new Date();
    const days = Math.floor((now.getTime() - a.getTime()) / 86400000);
    // Guard: pregnancy is bounded to ~42 weeks (294 days). If user typed their
    // birth date or another far-past date, don't try to render a nonsense
    // gestational timeline (previously crashed because sizeMap.find returned
    // undefined and downstream JSX dereferenced `.emoji`).
    if (days < 0 || days > 320) return null;
    const weeks = Math.floor(days / 7);
    const remDays = days % 7;
    const eddAdj = 280 + (cycle - 28);
    const edd = new Date(a.getTime() + eddAdj * 86400000);
    let trimester = 1;
    if (weeks >= 28) trimester = 3; else if (weeks >= 14) trimester = 2;
    const daysToDue = Math.ceil((edd.getTime() - now.getTime()) / 86400000);
    const progress = Math.max(0, Math.min(1, weeks / 40));
    // Baby size comparison by week
    const sizeMap: { max: number; label: string; emoji: string }[] = [
      { max: 4, label: "Poppy seed", emoji: "🌱" },
      { max: 6, label: "Sweet pea", emoji: "🫛" },
      { max: 8, label: "Raspberry", emoji: "🍒" },
      { max: 10, label: "Kumquat", emoji: "🍊" },
      { max: 12, label: "Lime", emoji: "🍋" },
      { max: 16, label: "Avocado", emoji: "🥑" },
      { max: 20, label: "Banana", emoji: "🍌" },
      { max: 24, label: "Ear of corn", emoji: "🌽" },
      { max: 28, label: "Eggplant", emoji: "🍆" },
      { max: 32, label: "Coconut", emoji: "🥥" },
      { max: 36, label: "Papaya", emoji: "🍈" },
      { max: 40, label: "Small pumpkin", emoji: "🎃" },
      { max: 99, label: "Watermelon", emoji: "🍉" },
    ];
    const size =
      sizeMap.find((s) => weeks <= s.max) ?? sizeMap[sizeMap.length - 1];
    return { weeks, remDays, trimester, edd: edd.toISOString().slice(0, 10), daysToDue, progress, size };
  }, [lmp, cycle]);

  const shareMsg = r ? `🤰 Pregnancy Update!\n\n📅 Gestational age: ${r.weeks}w ${r.remDays}d (Trimester ${r.trimester})\n👶 Baby is about the size of a ${r.size.label} ${r.size.emoji}\n🎉 EDD: ${r.edd} · ${r.daysToDue} days to go!\n\nTrack yours on https://drhint.com/age-calculator/gestational-pregnancy` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Gestational & Pregnancy
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">{DESC}</p>
      </section>
      <section className="mx-auto max-w-5xl px-4">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-rose-50/50 via-card to-amber-50/40 dark:from-rose-950/20 dark:to-amber-950/10 p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <TripleDateInput label="First Day of Last Period (LMP)" value={lmp} onChange={(v) => { setLmp(v); reset(); }} max={today} />
            <label className="flex flex-col gap-2" style={{ minHeight: 92 }}>
              <span className="text-sm font-semibold">Average Cycle Length (days)</span>
              <input type="number" min={21} max={45} value={cycle} onChange={(e) => { setCycle(parseInt(e.target.value) || 28); reset(); }} className={inputCls()} />
            </label>
          </div>
          <AnalyzeButton onClick={trigger} disabled={!canGo || analyzing} label="Calculate Pregnancy Timeline" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={280}>
            {revealed && r ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Gestational Age" value={`${r.weeks}w ${r.remDays}d`} />
                  <ResultCard label="Trimester" value={`T${r.trimester}`} sub={r.trimester === 1 ? "Weeks 1–13" : r.trimester === 2 ? "Weeks 14–27" : "Week 28+"} />
                  <ResultCard label="Estimated Due Date" value={r.edd} />
                  <ResultCard label="Days to Due Date" value={r.daysToDue.toLocaleString()} />
                </div>
                <div className="mt-6 rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">Trimester progress</span>
                    <span className="text-xs text-muted-foreground">{Math.round(r.progress * 100)}% of 40 weeks</span>
                  </div>
                  <div className="h-3 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400 transition-all duration-1000" style={{ width: `${r.progress * 100}%` }} />
                  </div>
                  <div className="mt-1.5 flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
                    <span>T1</span><span>T2</span><span>T3</span><span>EDD</span>
                  </div>
                </div>
                <div className="mt-6 rounded-2xl border border-rose-200/50 dark:border-rose-900/40 bg-gradient-to-br from-rose-50 to-amber-50 dark:from-rose-950/30 dark:to-amber-950/20 p-5 flex items-center gap-4" style={{ minHeight: 96 }}>
                  <div className="text-5xl">{r.size.emoji}</div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Your baby is about the size of a</div>
                    <div className="text-2xl font-black text-rose-600 dark:text-rose-300">{r.size.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Week {r.weeks} approximate comparison</div>
                  </div>
                </div>
                <WhatsAppShareButton message={shareMsg} label="Share Pregnancy Update" />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter your LMP and tap Calculate.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>
      <ContentSection title="How the LMP Method Works">
        <p>
          Gestational age counts from the <strong>first day of your Last Menstrual Period</strong>, which is roughly two
          weeks before conception. Doctors use LMP because it's a date women reliably remember, and it produces a
          consistent starting point across every pregnancy.
        </p>
        <p>
          The classic Naegele's rule adds 280 days (40 weeks) to LMP to produce the Estimated Due Date (EDD). Our
          calculator refines that: if your cycle is longer than 28 days, ovulation happens later, so we shift EDD forward
          by the same difference. Only about 5% of babies actually arrive on their EDD — the normal window is 37 to 42
          weeks.
        </p>
        <h3>Trimester milestones</h3>
        <ul>
          <li><strong>First trimester (1–13 weeks)</strong> — organ development, highest miscarriage risk, first ultrasound.</li>
          <li><strong>Second trimester (14–27 weeks)</strong> — anatomy scan around week 20, first fetal movements.</li>
          <li><strong>Third trimester (28+ weeks)</strong> — rapid weight gain, glucose screening, birth planning.</li>
        </ul>
        <p>
          After delivery, track your baby's growth with the main{" "}
          <Link to="/age-calculator" className="text-primary underline">Age Calculator hub</Link> or compare mother-and-baby
          health markers using our <Link to="/age-calculator/biological-health-bmi" className="text-primary underline">Biological & Health Age tool</Link>.
        </p>
      </ContentSection>
      <FaqBlock faqs={FAQS} />
      <section className="mx-auto max-w-5xl px-4">
        <PregnancyHubTabs activeCluster="standard" activeWeek={0} />
      </section>
      <SubHubGrid excludePath="/age-calculator/gestational-pregnancy" />
    </Layout>
  );
}